import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { ArrowLeft, User, Trophy, Save, CreditCard, Camera, Trash2, MapPin, Shield, GraduationCap, LogOut } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Keep in sync with backend CENTRAL_IL_LOCATIONS. Any of these puts the player on
// the Central Illinois leaderboard; "Outside Central Illinois" = global only.
const CENTRAL_IL_LOCATIONS = ["Bloomington", "Normal", "Champaign", "Urbana", "Peoria",
  "Springfield", "Decatur", "Pekin", "Lincoln", "Danville", "Central Illinois (other)"];
const LOCATION_OPTIONS = [...CENTRAL_IL_LOCATIONS, "Outside Central Illinois"];

export default function ProfileSettings() {
  const navigate = useNavigate();
  const { player, token, refresh, updateProfile, openBillingPortal, uploadAvatar, removeAvatar, logout } = usePlayerAuth();
  const [name, setName] = useState(player?.display_name || player?.name || "");
  const [location, setLocation] = useState(player?.location || "");
  const CLUB_OPTIONS = ["Peoria FC United", "JGSA", "Morton United FC"];
  const initialClub = player?.club_name || "";
  const [clubChoice, setClubChoice] = useState(
    initialClub ? (CLUB_OPTIONS.includes(initialClub) ? initialClub : "Other") : ""
  );
  const [clubCustom, setClubCustom] = useState(
    initialClub && !CLUB_OPTIONS.includes(initialClub) ? initialClub : ""
  );
  const [school, setSchool] = useState(player?.school_name || "");
  const [consent, setConsent] = useState(!!player?.share_consent);
  const [saving, setSaving] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileRef = useRef(null);
  const clubValue = clubChoice === "Other" ? clubCustom.trim() : clubChoice;

  const avatarSrc = player?.avatar_url ? `${BACKEND}${player.avatar_url}` : null;
  const initials = ((player?.display_name || player?.name || player?.email || "P").trim()[0] || "P").toUpperCase();

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)."); return; }
    setAvatarBusy(true);
    try {
      await uploadAvatar(file);
      toast.success("Profile photo updated!", { description: "It now shows on your profile and the leaderboard." });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo. Please try again.");
    } finally {
      setAvatarBusy(false);
    }
  };

  const onRemoveAvatar = async () => {
    setAvatarBusy(true);
    try {
      await removeAvatar();
      toast.success("Profile photo removed.");
    } catch (err) {
      toast.error("Could not remove photo. Please try again.");
    } finally {
      setAvatarBusy(false);
    }
  };


  const leaderboardPreview = (() => {
    const n = (name || "").trim();
    if (!n) return (player?.email || "player").split("@")[0].slice(0, 14);
    const parts = n.split(" ");
    return parts.length > 1 ? `${parts[0]} ${parts[1][0]}.` : parts[0];
  })();

  const toggleConsent = async () => {
    const next = !consent;
    setConsent(next);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/me/share-consent`, { share_consent: next }, { headers: { Authorization: `Bearer ${token}` } });
      await refresh?.();
      toast.success(next ? "Sharing on — teammates can see your highlights." : "Sharing off — your highlights are hidden from teammates.");
    } catch { setConsent(!next); toast.error("Couldn't update sharing. Please try again."); }
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Please enter a display name."); return; }
    if (!clubChoice) { toast.error("Please select your club — it shows on the leaderboard."); return; }
    if (clubChoice === "Other" && !clubCustom.trim()) { toast.error("Please type in your club name."); return; }
    if (!school.trim()) { toast.error("School name is required — it shows on the leaderboard."); return; }
    setSaving(true);
    try {
      await updateProfile({ display_name: trimmed, location, club_name: clubValue, school_name: school.trim() });
      toast.success("Profile updated!", { description: `The leaderboard will now show "${leaderboardPreview}".` });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const manageBilling = async () => {
    setBillingLoading(true);
    try {
      await openBillingPortal();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing. Please try again shortly.");
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] text-white with-bottom-nav">
      <header className="sticky top-0 z-30 bg-[#111]/85 backdrop-blur-xl border-b border-white/10 px-4 sm:px-6 pb-4 header-safe flex items-center justify-between gap-3">
        <button
          data-testid="profile-back-btn"
          onClick={() => navigate("/hub")}
          className="flex items-center gap-2 text-white/60 hover:text-white text-sm font-bold uppercase tracking-wider transition-colors py-2"
        >
          <ArrowLeft size={16} /> Back to Hub
        </button>
        <button
          data-testid="profile-logout-btn"
          onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-3 py-2 rounded-sm transition-colors"
        >
          <LogOut size={14} /> Log Out
        </button>
      </header>
      <div className="max-w-2xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-1"><User size={18} className="text-[#C41E24]" /><span className="text-[10px] uppercase tracking-widest text-[#C41E24] font-bold">Profile Settings</span></div>
          <h1 className="font-cover text-4xl sm:text-5xl uppercase tracking-tight">Player Name</h1>
          <p className="text-white/55 mt-2 text-sm">Set the player's name so it shows on the leaderboard — even if the Blueprint was purchased under a parent's name.</p>

          <div data-testid="profile-settings-card" className="mt-8 bg-[#141414] border border-white/10 rounded-xl p-6 sm:p-8">
            <div className="flex items-center gap-5 pb-6 mb-6 border-b border-white/10">
              <div className="relative shrink-0">
                <div data-testid="avatar-preview" className="w-20 h-20 rounded-full overflow-hidden bg-[#C41E24]/15 border-2 border-[#C41E24]/40 flex items-center justify-center">
                  {avatarSrc
                    ? <img loading="lazy" decoding="async" src={avatarSrc} alt="Profile" className="w-full h-full object-cover" />
                    : <span className="font-cover text-3xl text-[#C41E24]">{initials}</span>}
                </div>
                <button
                  data-testid="avatar-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={avatarBusy}
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#C41E24] hover:bg-[#A0161D] flex items-center justify-center border-2 border-[#141414] transition-colors disabled:opacity-60"
                  aria-label="Upload profile photo"
                >
                  <Camera size={15} />
                </button>
                <input ref={fileRef} data-testid="avatar-file-input" type="file" accept="image/*" onChange={onPickAvatar} className="hidden" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-bold uppercase tracking-widest text-white/60">Profile Photo</div>
                <p className="text-sm text-white/45 mt-1">Shows on your profile and next to your name on the leaderboard.</p>
                <div className="flex items-center gap-4 mt-2">
                  <button data-testid="avatar-change-btn" onClick={() => fileRef.current?.click()} disabled={avatarBusy}
                    className="text-xs font-bold uppercase tracking-wider text-[#C41E24] hover:text-white transition-colors disabled:opacity-60">
                    {avatarBusy ? "Uploading…" : avatarSrc ? "Change" : "Upload photo"}
                  </button>
                  {avatarSrc && (
                    <button data-testid="avatar-remove-btn" onClick={onRemoveAvatar} disabled={avatarBusy}
                      className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-white/40 hover:text-white transition-colors disabled:opacity-60">
                      <Trash2 size={13} /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <label htmlFor="display-name" className="block text-xs font-bold uppercase tracking-widest text-white/60 mb-2">Player Display Name</label>
            <input
              id="display-name"
              data-testid="display-name-input"
              type="text"
              value={name}
              maxLength={40}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Leo Barker"
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3.5 text-white text-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
            />

            <div className="mt-5 flex items-center gap-3 rounded-lg border border-white/10 bg-[#0d0d0d] px-4 py-3">
              <Trophy size={18} className="text-[#C41E24] shrink-0" />
              <div className="text-sm text-white/70">
                On the leaderboard: <span data-testid="leaderboard-name-preview" className="font-bold text-white">{leaderboardPreview}</span>
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="location" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/60 mb-2">
                <MapPin size={13} className="text-[#C41E24]" /> Location
              </label>
              <select
                id="location"
                data-testid="location-select"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3.5 text-white text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
              >
                <option value="">Select your area…</option>
                {LOCATION_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <p className="mt-2 text-xs text-white/40">Pick a Central Illinois town to appear on the <span className="text-[#C41E24] font-semibold">Central Illinois leaderboard</span>.</p>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="club" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/60 mb-2">
                  <Shield size={13} className="text-[#C41E24]" /> Club Name <span className="text-[#C41E24]">*</span>
                </label>
                <select
                  id="club"
                  data-testid="club-select"
                  value={clubChoice}
                  onChange={(e) => setClubChoice(e.target.value)}
                  className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3.5 text-white text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                >
                  <option value="">Select your club…</option>
                  {CLUB_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  <option value="Other">Other (type your own)</option>
                </select>
                {clubChoice === "Other" && (
                  <input
                    data-testid="club-custom-input"
                    value={clubCustom}
                    onChange={(e) => setClubCustom(e.target.value)}
                    placeholder="Type your club name"
                    autoFocus
                    className="mt-2 w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3.5 text-white text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                  />
                )}
              </div>
              <div>
                <label htmlFor="school" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/60 mb-2">
                  <GraduationCap size={13} className="text-[#C41E24]" /> School Name <span className="text-[#C41E24]">*</span>
                </label>
                <input
                  id="school"
                  data-testid="school-input"
                  value={school}
                  onChange={(e) => setSchool(e.target.value)}
                  placeholder="e.g. Normal West HS"
                  className="w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3.5 text-white text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-white/40">Your club &amp; school show next to your name and power the <span className="text-[#C41E24] font-semibold">Top Clubs &amp; Top Schools</span> rankings.</p>

            <div className="mt-5">
          <div data-testid="profile-consent" className="flex items-center gap-4 rounded-md border p-3.5" style={{ borderColor: consent ? "rgba(212,175,55,0.7)" : "rgba(255,255,255,0.12)", background: consent ? "rgba(212,175,55,0.08)" : "rgba(13,13,13,0.8)" }}>
            <span className="flex-1 text-sm leading-snug">
              <span className="block text-xs font-bold uppercase tracking-widest text-[#D4AF37] mb-1">Highlight Sharing</span>
              <span className="text-white/80">Allow my highlights to be shared with teammates</span>
            </span>
            <button type="button" role="switch" aria-checked={consent} data-testid="profile-consent-toggle" onClick={toggleConsent}
              className="shrink-0 w-16 h-11 flex items-center justify-center">
              <span className={`relative w-14 h-8 rounded-full transition-colors duration-200 ${consent ? "bg-[#D4AF37]" : "bg-white/15"}`}>
                <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200 ${consent ? "translate-x-6" : ""}`} />
              </span>
            </button>
          </div>
            </div>
            {player?.email && (
              <p className="mt-4 text-xs text-white/35">Account email: {player.email} (used for login — not shown publicly)</p>
            )}

            <button
              data-testid="save-profile-btn"
              onClick={save}
              disabled={saving}
              className="mt-6 w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider text-base px-6 py-4 rounded-lg transition-colors disabled:opacity-60"
            >
              <Save size={18} /> {saving ? "Saving…" : "Save Player Name"}
            </button>
          </div>

          {player?.has_billing && (
            <div data-testid="billing-card" className="mt-6 bg-[#141414] border border-white/10 rounded-xl p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-1"><CreditCard size={18} className="text-[#C41E24]" /><span className="text-[10px] uppercase tracking-widest text-[#C41E24] font-bold">Billing</span></div>
              <h2 className="font-headings text-2xl uppercase tracking-wide">Manage Enrollment</h2>
              <p className="text-white/55 mt-2 text-sm">Update your payment method, view receipts, or cancel — all handled securely by Stripe.</p>
              <button
                data-testid="manage-billing-btn"
                onClick={manageBilling}
                disabled={billingLoading}
                className="mt-5 w-full flex items-center justify-center gap-2 bg-white text-[#111] hover:bg-white/90 font-subheadings font-bold uppercase tracking-wider text-base px-6 py-4 rounded-lg transition-colors disabled:opacity-60"
              >
                <CreditCard size={18} /> {billingLoading ? "Opening…" : "Manage Enrollment"}
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
